import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { 
    Palette, BrainCircuit, Plus, Trash2, Download, X, 
    WandSparkles, Save, UploadCloud, CheckCircle, Loader2, ScanEye,
    ImageIcon, Layers, LayoutTemplate, Trophy, Quote
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Shared UI
import { 
    AppCard, AppButton, AppTextarea, BlackboxLoaderIcon, 
    SectionTitle 
} from '../../components/SharedUI';

// Services
import { generateVisualAnalysisLLM, callGeminiApi } from '../../services/aiService';

// Firebase Imports
import { ref, getDownloadURL, uploadBytes } from 'firebase/storage';

// Constants
const PLATFORM_SPECS = {
  'instagram-story': { title: "Instagram Story", w: 1080, h: 1920 },
  'instagram-post': { title: "Instagram Post", w: 1080, h: 1080 },
  'youtube-thumbnail': { title: "YouTube Thumbnail", w: 1920, h: 1080 },
  'facebook-banner': { title: "Facebook Banner", w: 1920, h: 1080 },
  'twitter-header': { title: "Twitter Header", w: 1500, h: 500 },
  'tiktok-cover': { title: "TikTok Video Cover", w: 1080, h: 1920 },
};

// --- STYLED COMPONENTS ---

const ConfidenceGauge = ({ value }) => {
    const radius = 30;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (value / 100) * circumference;
    
    return (
        <div className="relative flex items-center justify-center">
            <svg width="80" height="80" className="transform -rotate-90">
                <circle cx="40" cy="40" r={radius} stroke="#27272a" strokeWidth="6" fill="transparent" />
                <circle 
                    cx="40" cy="40" r={radius} 
                    stroke="#db2777" strokeWidth="6" fill="transparent" 
                    strokeDasharray={circumference} 
                    strokeDashoffset={offset} 
                    strokeLinecap="round"
                    className="transition-all duration-1000 ease-out"
                />
            </svg>
            <div className="absolute flex flex-col items-center">
                <span className="text-xl font-bold text-white">{value}%</span>
            </div>
        </div>
    );
};

const MIPostLoader = () => (
    <div className="relative flex flex-col items-center">
        <div className="relative">
            {/* Outer Glow Ring */}
            <motion.div 
                animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.5, 0.2] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute inset-0 bg-pink-500 blur-2xl rounded-full"
            />
            <BlackboxLoaderIcon className="w-16 h-16 text-white relative z-10" />
            
            {/* Horizontal Scan Line */}
            <motion.div 
                animate={{ top: ['0%', '100%', '0%'] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                className="absolute left-0 right-0 h-px bg-pink-400 shadow-[0_0_10px_#db2777] z-20"
            />
        </div>
        <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-pink-500 text-[10px] font-mono mt-6 tracking-[0.3em] uppercase font-bold"
        >
            Ingesting Visual Data...
        </motion.p>
    </div>
);

const LightboxUploader = ({ onFileSelect, uploadedFile, label, isLoading, isWinner }) => {
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => onFileSelect(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    return (
        <div className={`relative group w-full aspect-[2/3] bg-zinc-950 border-2 rounded-xl overflow-hidden transition-all flex flex-col items-center justify-center 
            ${isWinner ? 'border-pink-500 shadow-[0_0_30px_rgba(219,39,119,0.2)]' : 'border-zinc-800 hover:border-zinc-700'}
            ${isLoading ? 'ring-2 ring-pink-500/20 animate-pulse' : ''}`}>
            
            {/* Winner Badge */}
            {isWinner && (
                <div className="absolute top-0 inset-x-0 bg-gradient-to-r from-pink-600 to-purple-600 text-white text-[10px] font-bold uppercase tracking-widest py-1.5 text-center z-20 shadow-lg">
                    Recommended Choice
                </div>
            )}

            {uploadedFile && !isLoading ? (
                <>
                    <img src={uploadedFile} alt="Poster" className="w-full h-full object-cover transition-opacity group-hover:opacity-40" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
                            <p className="text-white text-xs font-bold uppercase tracking-widest flex items-center gap-2">
                                <UploadCloud size={14} className="text-pink-400" /> Replace Asset
                            </p>
                        </div>
                    </div>
                </>
            ) : (
                <div className="text-center p-6 flex flex-col items-center justify-center w-full h-full">
                    {isLoading ? (
                        <div className="relative">
                            {/* Premium MI Loader */}
                            <div className="absolute inset-0 bg-pink-500/20 blur-xl rounded-full animate-pulse"></div>
                            <BlackboxLoaderIcon className="w-12 h-12 text-pink-500 relative z-10" />
                            <p className="text-pink-500 text-[10px] font-mono mt-4 tracking-[0.2em] animate-pulse uppercase">Processing MI...</p>
                        </div>
                    ) : (
                        <>
                            <div className="w-14 h-14 bg-zinc-900 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform border border-zinc-800 group-hover:border-pink-500/50">
                                <ImageIcon size={22} className="text-zinc-600 group-hover:text-pink-500 transition-colors" />
                            </div>
                            <p className="text-zinc-400 text-sm font-bold tracking-tight uppercase">{label}</p>
                            <p className="text-zinc-600 text-[10px] mt-1 font-mono">DRAG & DROP</p>
                        </>
                    )}
                </div>
            )}
            <input 
                type="file" 
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" 
                onChange={handleFileChange} 
                accept="image/*" 
                disabled={isLoading}
            />
        </div>
    );
};

// --- PREMIUM OVERLAY COMPONENT ---
const PremiumVisualOverlay = ({ isVisible, currentStepIndex }) => {
    if (!isVisible) return null;

    const steps = [
        "Ingesting High-Res Poster Assets...",
        "Scanning Composition & Typography...",
        "Analyzing Color Psychology & Genre Fit...",
        "Comparing Visual Impact (A/B Comparison)...",
        "Generating Executive Recommendation..."
    ];

    const progress = Math.min(((currentStepIndex + 1) / steps.length) * 100, 100);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/90 backdrop-blur-xl animate-in fade-in duration-300">
            <div className="w-full max-w-lg p-8 relative">
                <div className="flex justify-center mb-10">
                    <div className="relative">
                        <div className="absolute inset-0 bg-pink-500 blur-2xl opacity-20 animate-pulse rounded-full"></div>
                        <Palette className="w-20 h-20 text-white relative z-10 animate-pulse" />
                    </div>
                </div>

                <div className="w-full h-1 bg-zinc-800 rounded-full mb-8 overflow-hidden">
                    <div 
                        className="h-full bg-gradient-to-r from-pink-600 to-purple-500 transition-all duration-500 ease-out"
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>

                <div className="space-y-3 font-mono text-xs">
                    {steps.map((step, index) => {
                        let statusIcon;
                        let statusColor;
                        
                        if (index < currentStepIndex) {
                            statusIcon = <CheckCircle size={14} />;
                            statusColor = "text-pink-500 opacity-50";
                        } else if (index === currentStepIndex) {
                            statusIcon = <Loader2 size={14} className="animate-spin" />;
                            statusColor = "text-white font-bold";
                        } else {
                            statusIcon = <div className="w-3 h-3 rounded-full border border-zinc-700" />;
                            statusColor = "text-zinc-600";
                        }

                        return (
                            <div key={index} className={`flex items-center gap-3 transition-all duration-300 ${statusColor}`}>
                                <div className="w-5 flex justify-center shrink-0">{statusIcon}</div>
                                <span>{step}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// --- VISUAL EDITOR MODAL ---
const VisualEditorModal = ({ isOpen, sourceImage, onSave, onClose, filmData, setNotification }) => {
    const [step, setStep] = useState('select');
    const [platform, setPlatform] = useState(null);
    const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
    const [tagline, setTagline] = useState('');
    const [isImageReady, setIsImageReady] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [imageLoadError, setImageLoadError] = useState(false);
    const dragStart = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
    const canvasRef = useRef(null);
    const imageRef = useRef(null);
    const containerRef = useRef(null);
  
    useEffect(() => {
      if (!isOpen) {
        setTimeout(() => {
          setStep('select'); setPlatform(null); setTransform({ x: 0, y: 0, scale: 1 }); setTagline('');
          setSuggestions([]); setIsImageReady(false); setImageLoadError(false);
        }, 300);
      }
    }, [isOpen]);
  
    useEffect(() => {
      if (isOpen && sourceImage && platform) {
        setIsImageReady(false); setImageLoadError(false);
        if (imageRef.current) { imageRef.current.onload = null; imageRef.current.onerror = null; }
        const img = new Image();
        const isBase64 = sourceImage.startsWith('data:');
        if (!isBase64) img.crossOrigin = 'anonymous';

        const handleLoad = () => { imageRef.current = img; setIsImageReady(true); setImageLoadError(false); setTimeout(() => drawCanvas(), 50); };
        const handleError = (e) => { console.error("Failed to load image:", e); setImageLoadError(true); setIsImageReady(false); };
        
        img.addEventListener('load', handleLoad);
        img.addEventListener('error', handleError);
        img.src = isBase64 ? sourceImage : sourceImage + (sourceImage.includes('?') ? '&' : '?') + 't=' + Date.now();
        
        return () => { img.removeEventListener('load', handleLoad); img.removeEventListener('error', handleError); };
      }
    }, [isOpen, sourceImage, platform]);
  
    const drawCanvas = useCallback(() => {
      const canvas = canvasRef.current; const img = imageRef.current; const container = containerRef.current;
      if (!canvas || !img || !platform || !isImageReady || !container) return;
      try {
        const { w, h } = platform; const ctx = canvas.getContext('2d');
        const containerRect = container.getBoundingClientRect(); const maxWidth = Math.min(containerRect.width - 40, 600);
        const aspectRatio = w / h;
        let displayWidth = maxWidth; let displayHeight = maxWidth / aspectRatio;
        if (displayHeight > 400) { displayHeight = 400; displayWidth = displayHeight * aspectRatio; }
        canvas.width = w; canvas.height = h; canvas.style.width = displayWidth + 'px'; canvas.style.height = displayHeight + 'px';
        ctx.fillStyle = '#18181b'; ctx.fillRect(0, 0, w, h);
        const centerX = w / 2; const centerY = h / 2;
        ctx.save();
        ctx.translate(centerX + transform.x, centerY + transform.y); ctx.scale(transform.scale, transform.scale); ctx.translate(-centerX, -centerY);
        const canvasAspect = w / h; const imgAspect = img.naturalWidth / img.naturalHeight;
        let drawWidth, drawHeight;
        if (imgAspect > canvasAspect) { drawHeight = h; drawWidth = imgAspect * drawHeight; } else { drawWidth = w; drawHeight = drawWidth / imgAspect; }
        const offsetX = (w - drawWidth) / 2; const offsetY = (h - drawHeight) / 2;
        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        ctx.restore();
        if (tagline.trim()) {
          const textBlockHeight = h * 0.2; ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'; ctx.fillRect(0, h - textBlockHeight, w, textBlockHeight);
          ctx.fillStyle = 'white'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          const fontSize = Math.max(24, Math.min(w / 20, 48)); ctx.font = `700 ${fontSize}px 'Inter', sans-serif`;
          const text = tagline.toUpperCase(); const textY = h - (textBlockHeight / 2); const maxWidth = w * 0.9;
          const words = text.split(' '); const lines = []; let currentLine = words[0];
          for (let i = 1; i < words.length; i++) {
            const testLine = currentLine + ' ' + words[i]; const metrics = ctx.measureText(testLine);
            if (metrics.width > maxWidth && currentLine.length > 0) { lines.push(currentLine); currentLine = words[i]; } else { currentLine = testLine; }
          }
          lines.push(currentLine);
          const lineHeight = fontSize * 1.2; const totalHeight = lines.length * lineHeight; const startY = textY - totalHeight / 2 + lineHeight / 2;
          lines.forEach((line, index) => { ctx.fillText(line, w / 2, startY + index * lineHeight); });
        }
      } catch (error) { console.error('Canvas drawing error:', error); setImageLoadError(true); }
    }, [platform, transform, tagline, isImageReady]);
  
    useEffect(() => { if (isImageReady && step === 'edit' && !imageLoadError) { drawCanvas(); } }, [isImageReady, step, transform, tagline, drawCanvas, imageLoadError]);
    
    // Mouse Event Handlers for Dragging
    const handleMouseDown = (e) => { if (imageLoadError) return; setIsDragging(true); const rect = e.currentTarget.getBoundingClientRect(); const x = e.clientX - rect.left; const y = e.clientY - rect.top; dragStart.current = { x, y, tx: transform.x, ty: transform.y }; };
    const handleMouseMove = (e) => { if (!isDragging || imageLoadError) return; const rect = e.currentTarget.getBoundingClientRect(); const x = e.clientX - rect.left; const y = e.clientY - rect.top; const deltaX = x - dragStart.current.x; const deltaY = y - dragStart.current.y; setTransform(t => ({ ...t, x: dragStart.current.tx + deltaX * 2, y: dragStart.current.ty + deltaY * 2 })); };
    const handleMouseUp = () => setIsDragging(false);
    const handleWheel = (e) => { if (imageLoadError) return; e.preventDefault(); const scaleFactor = e.deltaY > 0 ? 0.9 : 1.1; setTransform(t => ({ ...t, scale: Math.max(0.5, Math.min(3, t.scale * scaleFactor)) })); };
    
    const handleSelectPlatform = (key) => { setPlatform(PLATFORM_SPECS[key]); setStep('edit'); };
    const handleGenerateTagline = async () => { 
        setIsGenerating(true); 
        try { 
            const result = await callGeminiApi(`Generate 3 short, punchy taglines for a ${filmData?.genre || 'film'} titled "${filmData?.title || 'this film'}". Each tagline should be under 8 words. Return only the taglines, one per line.`); 
            const lines = result.split('\n').map(l => l.replace(/^[0-9\-."'\s]+/, '').trim()).filter(Boolean).slice(0, 3); 
            setSuggestions(lines); 
        } catch (error) { console.error('Failed to generate taglines:', error); } 
        finally { setIsGenerating(false); } 
    };
    
    const handleFinalSave = () => { if (!canvasRef.current || imageLoadError) return; try { const finalImage = canvasRef.current.toDataURL('image/jpeg', 0.95); const platformKey = Object.keys(PLATFORM_SPECS).find(key => PLATFORM_SPECS[key] === platform); onSave(finalImage, { platform: platformKey, title: platform.title }); } catch (error) { console.error('Failed to save canvas:', error); } };
    if (!isOpen) return null;
  
    return (
      <div className="fixed inset-0 bg-black/90 backdrop-blur-xl flex items-center justify-center z-50 p-4">
        <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
          {step === 'select' && (
            <div className="flex-1 overflow-y-auto p-8">
              <div className="flex justify-between items-center mb-8">
                  <h3 className="text-2xl font-bold text-white font-poppins">Select Format</h3>
                  <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-full"><X size={20} className="text-zinc-400"/></button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {Object.entries(PLATFORM_SPECS).map(([key, {title, w, h}]) => (
                  <button key={key} onClick={() => handleSelectPlatform(key)} disabled={!sourceImage} className="group p-6 bg-zinc-900 border border-zinc-800 rounded-xl text-left hover:border-pink-500 hover:bg-zinc-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                    <div className="flex justify-between items-start mb-4">
                        <LayoutTemplate className="text-zinc-500 group-hover:text-pink-400" />
                        <span className="text-[10px] font-mono text-zinc-500">{w}x{h}</span>
                    </div>
                    <p className="font-bold text-white text-lg">{title}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 'edit' && platform && (
            <div className="flex flex-col h-full">
              <div className="p-4 border-b border-zinc-800 flex justify-between items-center bg-zinc-900">
                  <div className="flex items-center gap-4">
                      <button onClick={() => setStep('select')} className="text-zinc-400 hover:text-white text-sm">← Back</button>
                      <div className="h-6 w-px bg-zinc-800"></div>
                      <h3 className="font-bold text-white">{platform.title} Editor</h3>
                  </div>
                  <div className="flex gap-2">
                      <AppButton onClick={onClose} variant="secondary">Cancel</AppButton>
                      <AppButton onClick={handleFinalSave} icon={Save} disabled={imageLoadError} className="bg-pink-600 hover:bg-pink-500">Save Asset</AppButton>
                  </div>
              </div>
              
              <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-0 overflow-hidden">
                {/* Canvas Area */}
                <div ref={containerRef} className="lg:col-span-8 bg-black flex items-center justify-center p-8 relative overflow-hidden" onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
                  {!isImageReady && !imageLoadError && <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="animate-spin"/> Loading Canvas...</div>}
                  {isImageReady && !imageLoadError && <canvas ref={canvasRef} className="shadow-2xl border border-zinc-800" onMouseDown={handleMouseDown} onWheel={handleWheel} style={{ cursor: isDragging ? 'grabbing' : 'grab' }} />}
                </div>

                {/* Controls Sidebar */}
                <div className="lg:col-span-4 bg-zinc-900 border-l border-zinc-800 p-6 flex flex-col gap-6 overflow-y-auto">
                  <div>
                    <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4">Transform</h4>
                    <div className="space-y-4">
                        <label className="block text-sm text-zinc-300">Zoom Scale</label>
                        <input type="range" min="0.5" max="3" step="0.1" value={transform.scale} onChange={e => setTransform(t => ({...t, scale: parseFloat(e.target.value)}))} className="w-full accent-pink-500" disabled={imageLoadError} />
                        <AppButton onClick={() => setTransform({x: 0, y: 0, scale: 1})} size="sm" variant="secondary" className="w-full" disabled={imageLoadError}>Reset View</AppButton>
                    </div>
                  </div>

                  <div className="border-t border-zinc-800 pt-6">
                    <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4">Text Overlay</h4>
                    <AppTextarea rows="3" placeholder="Enter tagline..." value={tagline} onChange={(e) => setTagline(e.target.value)} disabled={imageLoadError} className="mb-3" />
                    <AppButton onClick={handleGenerateTagline} disabled={isGenerating || imageLoadError} icon={WandSparkles} size="sm" variant="secondary" className="w-full">{isGenerating ? 'Dreaming...' : 'Generate Ideas'}</AppButton>
                    
                    {suggestions.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {suggestions.map((suggestion, i) => ( 
                            <button key={i} onClick={() => setTagline(suggestion)} className="w-full text-left text-xs p-3 bg-zinc-800 rounded hover:bg-zinc-700 text-zinc-300 border border-zinc-700 transition-colors">
                                "{suggestion}"
                            </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
};

const GraphicPreviewModal = ({ graphic, onClose }) => {
    if (!graphic) return null;
    return (
      <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div className="relative max-w-3xl w-full max-h-[90vh] flex flex-col bg-zinc-900 rounded-xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <div className="p-4 border-b border-zinc-800 flex justify-between items-center bg-zinc-950">
              <div>
                  <h3 className="text-lg font-bold font-poppins text-white">{graphic.title}</h3>
                  <p className="text-xs text-zinc-500 uppercase">{graphic.platform}</p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-full"><X size={20} className="text-zinc-400"/></button>
          </div>
          <div className="flex-grow flex items-center justify-center bg-black p-8">
             <img src={graphic.url} alt={graphic.title} className="max-w-full max-h-full object-contain shadow-lg"/>
          </div>
        </div>
      </div>
    );
};

// --- MAIN COMPONENT ---
const VisualContentTab = ({ filmData, setNotification, saveFilmData, storage, credits, deductCredit }) => {
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [previewingGraphic, setPreviewingGraphic] = useState(null);
  const [posterChoice, setPosterChoice] = useState('ai');
  
  const [loadingA, setLoadingA] = useState(false);
  const [loadingB, setLoadingB] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0); 

  const [posterABase64, setPosterABase64] = useState(null);
  const [posterBBase64, setPosterBBase64] = useState(null);

  const analysis = useMemo(() => filmData.visual_analysis || null, [filmData.visual_analysis]);
  const posterA = useMemo(() => filmData.poster_a || null, [filmData.poster_a]);
  const posterB = useMemo(() => filmData.poster_b || null, [filmData.poster_b]);
  const customGraphics = useMemo(() => filmData.custom_graphics || [], [filmData.custom_graphics]);

  // DERIVED: Which poster won?
  const winningPoster = useMemo(() => {
      if (!analysis?.recommendation) return null;
      return analysis.recommendation.toLowerCase().includes('poster a') || analysis.recommendation.toLowerCase().includes('version a') ? 'a' : 'b';
  }, [analysis]);

  const sourcePosterUrl = useMemo(() => {
    if (posterChoice === 'a') return posterA;
    if (posterChoice === 'b') return posterB;
    if (winningPoster === 'b' && posterB) return posterB;
    if (posterA) return posterA;
    if (posterB) return posterB;
    return null;
  }, [posterA, posterB, posterChoice, winningPoster]);

  const robustImageUpload = async (base64String, path, fileName) => {
    try {
        const response = await fetch(base64String);
        const blob = await response.blob();
        const fullPath = `${path}/${fileName}`;
        const storageRef = ref(storage, fullPath);
        await uploadBytes(storageRef, blob);
        return await getDownloadURL(storageRef);
    } catch (error) {
        console.error("Robust Upload Failed:", error);
        return null;
    }
  };

  useEffect(() => {
    let interval;
    if (analysisLoading) {
        setLoadingStepIndex(0);
        interval = setInterval(() => {
            setLoadingStepIndex(prev => (prev < 3 ? prev + 1 : prev));
        }, 2500); 
    }
    return () => clearInterval(interval);
  }, [analysisLoading]);

  const handlePosterUpload = async (posterKey, base64Data) => {
    if (!filmData.id) {
        setNotification({type: 'error', message: 'Please save your project before uploading images.'});
        return;
    }

    const setLoading = posterKey === 'poster_a' ? setLoadingA : setLoadingB;
    const setBase64 = posterKey === 'poster_a' ? setPosterABase64 : setPosterBBase64;
    
    setLoading(true);
    setBase64(base64Data); 
    setNotification({ type: 'info', message: 'Uploading poster to cloud...' });

    try {
        const imageUrl = await robustImageUpload(base64Data, `${filmData.id}/posters`, `${posterKey}_${Date.now()}.jpg`);
        if (imageUrl) {
            // FIX: Instead of spreading the stale 'filmData' from the closure, 
            // we rely on the parent's save function which should ideally handle 
            // partial updates, or we fetch the most recent state.
            await saveFilmData({ 
                ...filmData, 
                // We ensure the OTHER poster stays if it exists in the current filmData
                poster_a: posterKey === 'poster_a' ? imageUrl : filmData.poster_a,
                poster_b: posterKey === 'poster_b' ? imageUrl : filmData.poster_b
            }, { silent: true });
            
            setNotification({ type: 'success', message: 'Poster uploaded successfully.' });
        } else {
            setNotification({ type: 'error', message: 'Cloud upload failed.' });
        }
    } catch (error) {
         setNotification({ type: 'error', message: `Upload error: ${error.message}` });
    } finally {
        setLoading(false);
    }
};

  const handleAnalyze = async () => {
    const imageAForAnalysis = posterABase64 || posterA;
    const imageBForAnalysis = posterBBase64 || posterB;

    if (!imageAForAnalysis || !imageBForAnalysis) {
        setNotification({ type: 'error', message: 'Please upload both poster versions before analyzing.' });
        return;
    }

    if (credits < 3) {
        setNotification({ type: 'error', message: 'Insufficient credits. Poster Analysis requires 3 credits.' });
        return;
    }

    setAnalysisLoading(true);
    setLoadingStepIndex(0);

    try {
      await new Promise(r => setTimeout(r, 1000));
      const result = await generateVisualAnalysisLLM(filmData, imageAForAnalysis, imageBForAnalysis);
      setLoadingStepIndex(4);
      await new Promise(r => setTimeout(r, 800));
      await deductCredit(3);
      await saveFilmData({ ...filmData, visual_analysis: result });
      setNotification({ type: 'success', message: 'Visual analysis acquired.' });
    } catch (error) {
      setNotification({ type: 'error', message: `Analysis failed: ${error.message}.` });
    } finally {
      setAnalysisLoading(false);
      setLoadingStepIndex(0);
    }
  };
  
  const handleSaveGraphic = async (finalImageBase64, metadata) => {
    setIsEditorOpen(false);
    setNotification({ type: 'info', message: 'Saving asset to library...' });
    
    try {
      const imageUrl = await robustImageUpload(finalImageBase64, `${filmData.id}/custom_graphics`, `${metadata.platform}_${Date.now()}.jpg`);
      
      if (imageUrl) {
        const newGraphic = {
          id: `graphic_${Date.now()}`, 
          url: imageUrl, 
          platform: metadata.platform, 
          title: metadata.title,
        };
        const updatedGraphics = [...customGraphics, newGraphic];
        await saveFilmData({ ...filmData, custom_graphics: updatedGraphics });
        setNotification({ type: 'success', message: 'Asset secured.' });
      } else { 
          throw new Error("Upload to Firebase failed."); 
      }
    } catch (error) {
      setNotification({ type: 'error', message: `Could not save graphic: ${error.message}` });
    }
  };

  const handleDownloadGraphic = async (graphic) => {
    try {
      setNotification({ type: 'info', message: 'Downloading asset...' });
      const response = await fetch(graphic.url);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${filmData.title.replace(/ /g, '_')}_${graphic.platform}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      setNotification({ type: 'error', message: 'Download failed.' });
    }
  };

  const handleDeleteGraphic = async (graphicId) => {
      const updatedGraphics = customGraphics.filter(g => g.id !== graphicId);
      await saveFilmData({ ...filmData, custom_graphics: updatedGraphics });
      setNotification({ type: 'info', message: 'Asset deleted.' });
  };
  
  return (
    <div className="space-y-8 pb-20">
      <PremiumVisualOverlay 
        isVisible={analysisLoading} 
        currentStepIndex={loadingStepIndex} 
      />

      <GraphicPreviewModal graphic={previewingGraphic} onClose={() => setPreviewingGraphic(null)} />
      <VisualEditorModal 
          isOpen={isEditorOpen}
          sourceImage={sourcePosterUrl}
          onClose={() => setIsEditorOpen(false)}
          onSave={handleSaveGraphic}
          filmData={filmData}
          setNotification={setNotification}
      />
      
      <div className="flex justify-between items-center">
          <SectionTitle icon={Palette}>Visual Intelligence</SectionTitle>
          <AppButton onClick={handleAnalyze} disabled={!posterA || !posterB || analysisLoading} size="lg" icon={ScanEye} className="bg-pink-600 hover:bg-pink-500 shadow-lg shadow-pink-500/20">
            {analysisLoading ? 'Analyzing...' : 'Run A/B Analysis (3 Credits)'}
          </AppButton>
      </div>
      
      {/* --- A/B LAB SECTION --- */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* LEFT: LIGHTBOXES (5 Cols) */}
        <div className="lg:col-span-5 space-y-6">
            <div className="bg-zinc-950 p-6 rounded-2xl border border-zinc-800">
                <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                    <ImageIcon className="text-zinc-500" size={20} /> Source Materials
                </h3>
                <div className="grid grid-cols-2 gap-4">
                    <LightboxUploader 
                        onFileSelect={(b64) => handlePosterUpload('poster_a', b64)} 
                        uploadedFile={posterA} 
                        label="Poster A" 
                        isLoading={loadingA} 
                        isWinner={winningPoster === 'a'}
                    />
                    <LightboxUploader 
                        onFileSelect={(b64) => handlePosterUpload('poster_b', b64)} 
                        uploadedFile={posterB} 
                        label="Poster B" 
                        isLoading={loadingB} 
                        isWinner={winningPoster === 'b'}
                    />
                </div>
            </div>
        </div>

        {/* RIGHT: FORENSICS (7 Cols) */}
        <div className="lg:col-span-7">
            {analysis ? (
                <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 h-full flex flex-col animate-in fade-in slide-in-from-right-8 duration-700">
                    <div className="flex justify-between items-start mb-8">
                        <div>
                            <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1">Analysis Result</p>
                            <h2 className="text-4xl font-bold text-white font-poppins tracking-tight flex items-center gap-3">
                                {analysis.recommendation === 'Version A' ? 'Poster A Wins' : 'Poster B Wins'}
                                <Trophy className="text-yellow-500 fill-yellow-500 animate-bounce" size={28} />
                            </h2>
                        </div>
                        <div className="text-center">
                            <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2">Confidence</p>
                            <ConfidenceGauge value={analysis.confidence || 85} />
                        </div>
                    </div>

                    <div className="flex-1 bg-zinc-950/50 p-8 rounded-xl border border-zinc-800 mb-8 relative">
                        <Quote size={40} className="absolute top-4 right-4 text-zinc-800 opacity-50" />
                        <p className="text-zinc-300 leading-relaxed text-lg font-serif italic relative z-10">"{analysis.insight}"</p>
                    </div>

                    <div>
                        <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-3">Asset Generation Source</p>
                        <div className="flex gap-2 p-1 bg-black/40 rounded-lg border border-zinc-800">
                            <button onClick={() => setPosterChoice('ai')} className={`flex-1 py-3 rounded-md text-xs font-bold uppercase tracking-wider transition-all ${posterChoice === 'ai' ? 'bg-pink-600 text-white shadow-lg' : 'text-zinc-500 hover:text-white'}`}>AI Recommendation</button>
                            <button onClick={() => setPosterChoice('a')} className={`flex-1 py-3 rounded-md text-xs font-bold uppercase tracking-wider transition-all ${posterChoice === 'a' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-white'}`}>Force Poster A</button>
                            <button onClick={() => setPosterChoice('b')} className={`flex-1 py-3 rounded-md text-xs font-bold uppercase tracking-wider transition-all ${posterChoice === 'b' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-white'}`}>Force Poster B</button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-zinc-900/30 border-2 border-dashed border-zinc-800 rounded-2xl h-full flex flex-col items-center justify-center text-center p-8">
                    <BrainCircuit className="w-16 h-16 text-zinc-700 mb-4" />
                    <h3 className="text-xl font-bold text-zinc-500">Forensic Lab Offline</h3>
                    <p className="text-zinc-600 mt-2 text-sm max-w-sm">Upload two distinct poster concepts to initialize the visual impact comparison engine.</p>
                </div>
            )}
        </div>
      </div>

      {/* --- ASSET FACTORY SECTION --- */}
      <div className="border-t border-zinc-800 pt-8 mt-4">
        <div className="flex justify-between items-center mb-6">
          <div>
              <h3 className="text-xl font-bold font-poppins text-white">Asset Factory</h3>
              <p className="text-zinc-500 text-sm">Generated social deliverables based on winning creative.</p>
          </div>
          <AppButton onClick={() => setIsEditorOpen(true)} disabled={!sourcePosterUrl} icon={Plus} variant="secondary">
            Create Asset
          </AppButton>
        </div>
        
        {customGraphics.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
               {customGraphics.map((graphic) => (
                 <div key={graphic.id} className="group relative aspect-[9/16] bg-black rounded-lg overflow-hidden border border-zinc-800 hover:border-pink-500/50 transition-all cursor-pointer shadow-lg" onClick={() => setPreviewingGraphic(graphic)}>
                    <img src={graphic.url} alt={graphic.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                    
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                        <p className="text-white font-bold text-xs truncate mb-2">{graphic.title}</p>
                        <div className="flex gap-2">
                            <button onClick={(e) => { e.stopPropagation(); handleDownloadGraphic(graphic); }} className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white py-1.5 rounded text-[10px] font-bold flex items-center justify-center gap-1">
                                <Download size={10} /> Save
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteGraphic(graphic.id); }} className="p-1.5 bg-red-900/50 hover:bg-red-900 text-red-200 rounded">
                                <Trash2 size={12} />
                            </button>
                        </div>
                    </div>
                 </div>
               ))}
            </div>
        ) : (
           <div className="text-center py-12 border-2 border-dashed border-zinc-800 rounded-xl bg-zinc-900/20">
             <Layers className="w-12 h-12 mx-auto mb-3 text-zinc-700"/>
             <p className="text-zinc-500 text-sm font-medium">Library Empty</p>
             {!sourcePosterUrl && <p className="text-zinc-600 text-xs mt-1">Upload a poster to start generating assets.</p>}
           </div>
        )}
     </div>
    </div>
  );
};

export default VisualContentTab;